import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import { getCurrentGatewayUrl } from '../../../utils/environmentDetection';
import { getAuthHeaders } from '../../../utils/authHeaders';
import { useCopilotSettingsRefreshStore } from '../../../store/copilotSettingsRefreshStore';

type RoleInput = {
  name?: string;
  description?: string;
  loginUrl?: string;
};

const orchestratorClientOptions = () => ({
  baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
});

const markRoleQueuesRefresh = () =>
  useCopilotSettingsRefreshStore
    .getState()
    .markSettingsRefreshFromRoleCredentialQueues();

export function useTargetRoleActions({
  applicationId,
  tenantId,
  refetchRoles,
}: {
  applicationId?: string;
  tenantId?: string;
  refetchRoles: () => Promise<unknown>;
}) {
  const t = useTranslations('settings.targetSettings.roleAnalysis');

  const handleCreateRole = useCallback(
    async (roleData: RoleInput & { name: string }) => {
      if (!applicationId || !tenantId) {
        throw new Error('No target selected');
      }

      try {
        const authHeaders = await getAuthHeaders();
        const response = await applicationsClient.createRole({
          params: { id: applicationId },
          body: roleData,
          overrideClientOptions: orchestratorClientOptions(),
          extraHeaders: { ...authHeaders },
        });

        if (response.status === 200) {
          const body = response.body as { role: ApplicationRoleDto };

          markRoleQueuesRefresh();

          return body.role;
        }

        throw new Error('Failed to create role');
      } catch (error) {
        console.error('Failed to create role:', error);

        throw error;
      }
    },
    [applicationId, tenantId],
  );

  const handleUpdateRole = useCallback(
    async (roleId: string, roleData: RoleInput) => {
      if (!applicationId || !tenantId) {
        throw new Error('No target selected');
      }

      try {
        const authHeaders = await getAuthHeaders();
        const response = await applicationsClient.updateRole({
          params: { id: applicationId, roleId },
          body: roleData,
          overrideClientOptions: orchestratorClientOptions(),
          extraHeaders: { ...authHeaders },
        });

        if (response.status === 200) {
          const body = response.body as { role: ApplicationRoleDto };

          markRoleQueuesRefresh();

          return body.role;
        }

        throw new Error('Failed to update role');
      } catch (error) {
        console.error('Failed to update role:', error);

        throw error;
      }
    },
    [applicationId, tenantId],
  );

  const handleDeleteRole = useCallback(
    async (roleId: string) => {
      if (!applicationId || !tenantId) {
        throw new Error('No target selected');
      }

      try {
        const authHeaders = await getAuthHeaders();
        const response = await applicationsClient.deleteRole({
          params: { id: applicationId, roleId },
          overrideClientOptions: orchestratorClientOptions(),
          extraHeaders: { ...authHeaders },
        });

        if (response.status !== 200) {
          throw new Error('Failed to delete role');
        }
      } catch (error) {
        console.error('Failed to delete role:', error);

        throw error;
      }
    },
    [applicationId, tenantId],
  );

  const handleRoleAnalyze = useCallback(
    async (roleId: string) => {
      if (!applicationId || !tenantId) {
        throw new Error('No target selected');
      }

      try {
        const authHeaders = await getAuthHeaders();
        const response = await applicationsClient.analyzeRole({
          params: { id: applicationId, roleId },
          overrideClientOptions: orchestratorClientOptions(),
          extraHeaders: { ...authHeaders },
        });

        if (response.status === 200 && response.body.jobId) {
          toasterApi.success({
            title: t('startedTitle'),
            description: t('startedDescription'),
          });
          await refetchRoles();

          return { jobId: response.body.jobId };
        }

        if (response.status === 409) {
          await refetchRoles();
          toasterApi.info({
            title: t('inProgressTitle'),
            description: t('inProgressDescription'),
          });

          return { jobId: 'already-in-progress' };
        }

        throw new Error('Failed to start role analysis');
      } catch (error) {
        console.error('Failed to analyze role:', error);

        throw error;
      }
    },
    [applicationId, tenantId, refetchRoles, t],
  );

  return {
    handleCreateRole,
    handleUpdateRole,
    handleDeleteRole,
    handleRoleAnalyze,
  };
}
